// ./src/utils/api/ApiService.ts
import { apolloClient } from "../../config/apollo";
import { OpResult } from "@sdflc/api-helpers";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { isAuthErrorCode, triggerAuthError } from "./authErrorHandler";

/**
 * Generic API service class that wraps GraphQL operations with OpResult
 */
export class ApiService {
  /**
   * Check result for auth errors and trigger global handler if needed
   */
  private static checkAuthError(result: { code?: number; errors?: any[] }): void {
    const code = result.code ?? OP_RESULT_CODES.OK;

    if (isAuthErrorCode(code)) {
      // Extract error message if available
      const message = result.errors?.[0]?.errors?.[0] || "Authentication required";
      triggerAuthError(code, message);
    }
  }

  /**
   * Execute a GraphQL mutation and wrap the result in OpResult
   */
  static async executeMutation(
    mutation: any,
    variables: any = {},
    options: any = {}
  ): Promise<OpResult> {
    try {
      const response: any = await apolloClient.mutate({
        mutation,
        variables,
        ...options,
      });

      // Extract the mutation result (assuming single mutation)
      const mutationName = Object.keys(response.data || {})[0];
      const result = response.data?.[mutationName];

      if (!result) {
        return new OpResult({
          code: OP_RESULT_CODES.FAILED,
          data: null,
          errors: [{ name: "", errors: ["No data returned from server"] }],
        });
      }

      // Check for auth errors in successful response
      this.checkAuthError(result);

      // The GraphQL response should already be in OpResult format
      return new OpResult({
        code: result.code || OP_RESULT_CODES.OK,
        data: result.data,
        errors: result.errors || [],
      });
    } catch (error: any) {
      console.error("GraphQL mutation error:", error);

      // Handle network errors
      if (error.networkError) {
        // Check for 401/403 HTTP status in network error
        const statusCode = error.networkError.statusCode;
        if (statusCode === 401 || statusCode === 403) {
          triggerAuthError(
            OP_RESULT_CODES.UNAUTHORIZED,
            "Session expired. Please sign in again."
          );
        }

        return new OpResult({
          code: OP_RESULT_CODES.NETWORK_ERROR,
          data: null,
          errors: [
            {
              name: "",
              errors: [error.networkError.message || "Network error occurred"],
            },
          ],
        });
      }

      // Handle GraphQL errors
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const errors = error.graphQLErrors.map((gqlError: any) => ({
          name: gqlError.extensions?.field || "",
          errors: [gqlError.message],
        }));

        // Check for auth-related GraphQL errors
        const authError = error.graphQLErrors.find(
          (gqlError: any) =>
            gqlError.extensions?.code === "UNAUTHENTICATED" ||
            gqlError.extensions?.code === "FORBIDDEN"
        );
        if (authError) {
          triggerAuthError(OP_RESULT_CODES.UNAUTHORIZED, authError.message);
        }

        return new OpResult({
          code: OP_RESULT_CODES.FAILED,
          data: null,
          errors,
        });
      }

      // Generic error
      return new OpResult({
        code: OP_RESULT_CODES.EXCEPTION,
        data: null,
        errors: [
          { name: "", errors: [error.message || "Unknown error occurred"] },
        ],
      });
    }
  }

  /**
   * Execute a GraphQL query and wrap the result in OpResult
   */
  static async executeQuery(
    query: any,
    variables: any = {},
    options: any = {}
  ): Promise<OpResult> {
    try {
      const response: any = await apolloClient.query({
        query,
        variables,
        ...options,
        fetchPolicy: options.fetchPolicy || "network-only", // Allow override
      });

      // Extract the query result (assuming single query)
      const queryName = Object.keys(response.data || {})[0];
      const result: any = response.data?.[queryName] ?? "";

      if (!result) {
        return new OpResult({
          code: OP_RESULT_CODES.FAILED,
          data: null,
          errors: [{ name: "", errors: ["No data returned from server"] }],
        });
      }

      // Check for auth errors in successful response
      this.checkAuthError(result);

      // The GraphQL response should already be in OpResult format
      return new OpResult({
        code: result.code || OP_RESULT_CODES.OK,
        data: result.data,
        errors: result.errors || [],
      });
    } catch (error: any) {
      console.error("GraphQL query error:", error);

      // Handle network errors
      if (error.networkError) {
        // Check for 401/403 HTTP status in network error
        const statusCode = error.networkError.statusCode;
        if (statusCode === 401 || statusCode === 403) {
          triggerAuthError(
            OP_RESULT_CODES.UNAUTHORIZED,
            "Session expired. Please sign in again."
          );
        }

        return new OpResult({
          code: OP_RESULT_CODES.NETWORK_ERROR,
          data: null,
          errors: [
            {
              name: "",
              errors: [error.networkError.message || "Network error occurred"],
            },
          ],
        });
      }

      // Handle GraphQL errors
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const errors = error.graphQLErrors.map((gqlError: any) => ({
          name: gqlError.extensions?.field || "",
          errors: [gqlError.message],
        }));

        // Check for auth-related GraphQL errors
        const authError = error.graphQLErrors.find(
          (gqlError: any) =>
            gqlError.extensions?.code === "UNAUTHENTICATED" ||
            gqlError.extensions?.code === "FORBIDDEN"
        );
        if (authError) {
          triggerAuthError(OP_RESULT_CODES.UNAUTHORIZED, authError.message);
        }

        return new OpResult({
          code: OP_RESULT_CODES.FAILED,
          data: null,
          errors,
        });
      }

      // Generic error
      return new OpResult({
        code: OP_RESULT_CODES.EXCEPTION,
        data: null,
        errors: [
          { name: "", errors: [error.message || "Unknown error occurred"] },
        ],
      });
    }
  }

  /**
   * Clear user session data (cookies are handled by server)
   */
  static clearSession() {
    localStorage.removeItem("formsubmits_user");
    // Reset Apollo cache to clear any cached user data
    apolloClient.resetStore();
  }
}